import { useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { prefersReducedMotion } from "@/lib/motion-variants";

/**
 * Wraps an existing button/link with a cursor-following radial glow, a shine
 * sweep, and a subtle magnetic pull toward the pointer. Caller keeps full
 * control of background/padding/shadow via `className` — this only adds the
 * interactive layer.
 */
export const GlowCTA = ({ to, href, onClick, type = "button", children, className = "", magnetic = true, ...rest }) => {
  const ref = useRef(null);
  const reduced = prefersReducedMotion();

  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el || reduced) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.setProperty("--glow-x", `${x}px`);
    el.style.setProperty("--glow-y", `${y}px`);
    if (magnetic) {
      const dx = (x - rect.width / 2) * 0.15;
      const dy = (y - rect.height / 2) * 0.15;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    }
  }, [magnetic, reduced]);

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--glow-x", "50%");
    el.style.setProperty("--glow-y", "50%");
    if (magnetic) el.style.transform = "translate(0, 0)";
  }, [magnetic]);

  const classes = `glow-cta ${className}`;
  const inner = (
    <>
      <span className="glow-cta__sheen" aria-hidden="true" />
      <span className="glow-cta__glow" aria-hidden="true" />
      <span className="relative flex items-center gap-2">{children}</span>
    </>
  );

  const shared = { ref, onMouseMove, onMouseLeave, className: classes, ...rest };

  if (to) return <Link to={to} {...shared}>{inner}</Link>;
  if (href) return <a href={href} {...shared}>{inner}</a>;
  return <button type={type} onClick={onClick} {...shared}>{inner}</button>;
};

export default GlowCTA;
